import { useI18n } from '@/composables';
import { useStore } from '@/store';

const oppositePredicates = ['NOT_UNIQUE', 'IS_NOT_BOT', 'NOT_EQUALS', 'NOT_CONTAINS'];

export const isOppositeFlowRulePredicate = (predicate: string) =>
  oppositePredicates.includes(predicate);

type FlowRuleNameItem = {
  name?: string;
  conditions: Record<string, unknown>;
};

type FlowRuleNamePart = {
  name: string;
  predicate?: string;
  count?: number;
};

const zeroTimeFormat = (time: number) => (time < 10 ? `0${time}` : `${time}`);

export const useFlowRuleName = () => {
  const store = useStore();
  const { t } = useI18n();

  const buildFlowRuleName = (
    item: FlowRuleNameItem,
    asString = true,
    hideNumber = false,
  ): string | FlowRuleNamePart[] => {
    if (item.name) {
      return item.name;
    }

    const conditions = Object.keys(item.conditions).map((key) => {
      const condition = item.conditions[key] as {
        values?: unknown;
        predicate?: string;
      };
      const values = condition.values;

      if (typeof values === 'function') {
        return condition;
      }

      const value = Array.isArray(values) ? values : [values];
      (value as { target?: string; predicate?: string }).target = key;
      (value as { target?: string; predicate?: string }).predicate = condition.predicate;
      return value;
    });

    const result: FlowRuleNamePart[] = [];

    conditions.forEach((entry: unknown) => {
      const conditionEntry = entry as Array<unknown> & {
        target?: string;
        predicate?: string;
      };

      if (typeof conditionEntry[0] === 'object') {
        for (const index in conditionEntry) {
          const nested = conditionEntry[index] as {
            predicate?: string;
            parameter?: string;
            values?: string[];
            from?: { hour: number; minute: number };
            to?: { hour: number; minute: number };
            perHour?: unknown;
            perDay?: unknown;
            perMonth?: unknown;
          };

          if (nested && typeof nested === 'object') {
            if (conditionEntry.target === 'timeOfDay' && nested.from && nested.to) {
              result.push({
                name: `${zeroTimeFormat(nested.from.hour)}:${zeroTimeFormat(nested.from.minute)} - ${zeroTimeFormat(nested.to.hour)}:${zeroTimeFormat(nested.to.minute)}`,
                predicate: nested.predicate,
              });
              return;
            }

            if (conditionEntry.target === 'visitCap') {
              const labels = Object.keys(nested).map((capKey) => {
                switch (capKey) {
                  case 'perHour':
                    return t('For 1 hour');
                  case 'perDay':
                    return t('For 24 hours');
                  case 'perMonth':
                    return t('For 1 month');
                  default:
                    return undefined;
                }
              });
              const firstOption = labels[0];

              if (typeof firstOption !== 'undefined') {
                result.push({
                  name: `${firstOption} ${(!hideNumber && labels.length > 1 && `(+${labels.length - 1}) `) || ''}`,
                  predicate: nested.predicate,
                  count: labels.length - 1,
                });
              }
              return;
            }

            let value = nested.values?.[0];
            if (value === '' || value === `[${t('Empty Value')}]`) {
              value = `[${t('Empty')}]`;
            }

            if (typeof value !== 'undefined') {
              const name = `${nested.parameter || t('Empty')}=${value} ${(!hideNumber && nested.values && nested.values.length > 1 && `(+${nested.values.length - 1}) `) || ''}`;
              result.push({
                name,
                predicate: nested.predicate,
                count: nested.values ? nested.values.length - 1 : 0,
              });
            }
          }
        }
      } else if (conditionEntry.length > 0) {
        if (conditionEntry[0] === '' || conditionEntry[0]) {
          if (conditionEntry.target !== 'timezone') {
            let name = '';
            let predicate = conditionEntry.predicate;

            switch (conditionEntry[0]) {
              case 'UNIQUE':
                name =
                  conditionEntry.target === 'uniqOfferViewer'
                    ? t('Is Not Recurring')
                    : t('Unique');
                break;
              case 'NOT_UNIQUE':
                name =
                  conditionEntry.target === 'uniqOfferViewer'
                    ? t('Is Recurring')
                    : t('Non-Unique');
                predicate = 'NOT_UNIQUE';
                break;
              case 'IS_NOT_BOT':
                name = t('Not bot');
                predicate = 'IS_NOT_BOT';
                break;
              case 'IS_NOT_WEB_VIEW':
                name = t('Not WebView');
                predicate = 'NOT_EQUALS';
                break;
              case 'IS_WEB_VIEW':
                name = t('Is WebView');
                predicate = 'IS_WEB_VIEW';
                break;
              case 'IS_BOT':
                name = t('Bot');
                break;
              case 'IS_CRAWLER':
                name = t('Is Crawler');
                break;
              case 'IS_NOT_CRAWLER':
                name = t('Is Not Crawler');
                predicate = 'NOT_EQUALS';
                break;
              case 'IS_PROXY':
                name = t('Is Proxy');
                break;
              case 'IS_IP_DATACENTER':
                name = t('IPs From Data Centers');
                break;
              case 'IS_NOT_IP_DATACENTER':
                name = t('IPs Not From Data Centers');
                predicate = 'NOT_EQUALS';
                break;
              case 'IS_IP_BLACKLISTED':
                name = t('IPs From Blacklists');
                break;
              case 'IS_NOT_IP_BLACKLISTED':
                name = t('IPs Not From Blacklists');
                predicate = 'NOT_EQUALS';
                break;
              case 'IP_WITH_FREQUENT_CAMPAIGN_EVENTS':
                name = t('Frequent in campaign');
                break;
              case 'IP_NOT_WITH_FREQUENT_CAMPAIGN_EVENTS':
                name = t('Not Frequent in campaign');
                predicate = 'NOT_EQUALS';
                break;
              case 'IP_WITH_FREQUENT_EVENTS':
                name = t('Frequent');
                break;
              case 'IP_NOT_WITH_FREQUENT_EVENTS':
                name = t('Not Frequent');
                predicate = 'NOT_EQUALS';
                break;
              case 'IS_NOT_PROXY':
                name = t('Not Proxy');
                predicate = 'NOT_EQUALS';
                break;
              case 'IS_EMPTY':
                name = t('Is Empty');
                break;
              case 'IS_NOT_EMPTY':
                name = t('Is Not Empty');
                predicate = 'NOT_EQUALS';
                break;
              default: {
                let firstOption = String(conditionEntry[0]).trim();
                if (firstOption === '') {
                  firstOption = `[ ${t('Empty')} ]`;
                }
                if (conditionEntry.target === 'country') {
                  firstOption =
                    store.countryListPlusGlobal.find(
                      (country) => country?.code === firstOption,
                    )?.name || firstOption;
                }
                name = `${firstOption} ${(!hideNumber && conditionEntry.length > 1 && `(+${conditionEntry.length - 1}) `) || ''}`;
              }
            }

            result.push({
              name,
              predicate,
              count: conditionEntry.length - 1,
            });
          }
        } else {
          result.push({
            name: `${conditionEntry.target} (${conditionEntry.length - 1}) `,
            predicate: 'EQUALS',
          });
        }
      }
    });

    if (asString) {
      return result.map((part) => part.name).join(' | ');
    }

    return result;
  };

  return {
    buildFlowRuleName,
    isOppositeFlowRulePredicate,
  };
};
